import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { t_DataIdentifier } from '../did-data-retrieval/did-data-retrieval.service';
import { GlobalService, _baseUrl } from '../../helper/global-helper';

@Injectable({
    providedIn: 'root',
})
export class DidConfigService implements OnDestroy {
    private baseUrl: string;
    machinenumber: string = "";
    line: string = '';

    public ecuList?: t_DidConfig = [];
    public didMapping: t_DidConfig = [];

    constructor(private http: HttpClient, private global: GlobalService) {
        this.baseUrl = _baseUrl;
        this.global.machineNumber.subscribe({
            next: newValue => {this.machinenumber = newValue.number;}
        });
        this.global.machineInfos.subscribe({
            next: newValue => {this.line = newValue.line;}
        });
    }

    ngOnDestroy(): void {
        this.global.cancelPendingRequests();
    }

    public initService() {
        this.loadEcuList().then(() => {
            this.ecuList!.forEach((ecu) => {
                this.setEcuDidMapping(ecu.ecu_id);
            });
        });
    }

    public loadEcuList(): Promise<void> {
        return new Promise((resolve) => {
            this.getEcuList(this.machinenumber, this.line).subscribe({
                next: (response) => {
                    response.forEach((ecu) => {
                        if (this.ecuList?.find((ecuList_ecu) => ecuList_ecu.ecu_id == ecu.ecu_id)) {
                            //console.log('Already loaded Ecu Id in Ecu List: ' + ecu.ecu_id);
                        } else {
                            this.ecuList?.push({
                                ecu_id: ecu.ecu_id,
                                ecu_name: ecu.ecu_name,
                                ecu_dids: [],
                            });
                        }
                    });
                },
                complete() {
                    resolve();
                },
            });
        });
    }

    public async setEcuDidMapping(ecuId: number): Promise<void> {
        return new Promise((resolve) => {
            let resp: t_DidConfig = [];
            this.getEcuDidMappingByMachineNumber(this.machinenumber, this.line).subscribe({
                next: (response: t_DidConfig) => {
                    resp = response;
                },
                complete: () => {
                    let existingMappingIndex: number = this.didMapping.findIndex((ecu) => {
                        return ecu.ecu_id == ecuId;
                    });
                    if (existingMappingIndex == -1) {
                        // No Mapping for ECU loaded
                        let index = resp.findIndex((ecu) => {return ecu.ecu_id == ecuId;});
                        if( index != -1) {
                            this.didMapping.push(resp[index]);
                        } else {
                            console.error('No information found for ECU: ' + ecuId);
                        }
                    } else {
                        // Mapping for ecu already loaded
                        let index = resp.findIndex((ecu) => {return ecu.ecu_id == ecuId;});
                        this.updateEcuInDidMapping(resp[index], existingMappingIndex);
                    }
                    resolve();
                },
            });
        });
    }

    private updateEcuInDidMapping(ecuDidMapping: t_Ecu, ecuIndex: number) {
        this.didMapping[ecuIndex].ecu_dids = ecuDidMapping.ecu_dids;
    }

    public getDataIdentifierOfDataName(dataName: string): t_DataIdentifier {
        let _ecuId: number = this.getEcuId(dataName);
        let _dataId: number | undefined;

        let targetEcuConfig = this.didMapping.find((ecu) => ecu.ecu_id == _ecuId);
        if (!targetEcuConfig) {
            console.error('No targetEcuConfig found for ' + dataName);
        }

        _dataId = targetEcuConfig?.ecu_dids.find((ecu_dids) => ecu_dids.did_name == dataName)?.did;
        if (!_dataId) {
            console.error('No Dataidentifier found for ' + dataName);
        }

        let identifier: t_DataIdentifier;
        if (_dataId != undefined) {
            identifier = { ecu_id: _ecuId, data_id: _dataId };
        } else {
            identifier = { ecu_id: _ecuId, data_id: _dataId! };
        }

        return identifier;
    }

    public getEcuId(dataName: string): number {
        let _ecuId!: number;
        for(let i = 0; i < this.didMapping.length; i++){
            for(let j = 0; j < this.didMapping[i].ecu_dids.length; j++){
                if(this.didMapping[i].ecu_dids[j].did_name == dataName){
                    _ecuId = this.didMapping[i].ecu_id; 
                }   
            }     
        }
        return _ecuId;
    }

    public getEcuIdFromDID(did: number): number {
        let _ecuId!: number;
        for(let i = 0; i < this.didMapping.length; i++){
            for(let j = 0; j < this.didMapping[i].ecu_dids.length; j++){
                if(this.didMapping[i].ecu_dids[j].did == did){
                    _ecuId = this.didMapping[i].ecu_id; 
                }   
            }     
        }
        return _ecuId;
    }

    private getEcuIdFromDataName(dataName: string): number {
        // Regex to look for a hex number in the function name, schema like *___ID_78->* = 78
        const regex = /ID_([0-9A-Fa-f]+)->/;
        const match = dataName.match(regex);
        if (match && match[1]) {
            return parseInt(match[1], 16);
        } else {
            throw new Error('Could not get ID from dataName name: ' + dataName);
        }
    }

    public getDidInformation(identifier: t_DataIdentifier): t_Did | undefined {
        let response: t_Did | undefined = this.didMapping
            .find((ecu) => ecu.ecu_id == identifier.ecu_id)
            ?.ecu_dids.find((data) => data.did == identifier.data_id);
        if (response == undefined) {
            //console.log('No information found for ECU: ' + identifier.ecu_id + ' Data ID: ' + identifier.data_id);
        }
        return response;
    }

    private subject = new Subject<any>();
    setClearList() {
        this.subject.next('');
    }
    getClearList() {
        return this.subject.asObservable();
    }


    // ===============================================
    // ================ API CALLS ====================
    // ===============================================

    public getEcuList(machinenumber: string, line: string): Observable<t_DidConfig> {
        return this.http.get<t_DidConfig>(this.baseUrl + 'didconfig?machinenumber=' + machinenumber + '&productline=' + line + 'line&eculist=true', {
            observe: 'body',
            responseType: 'json',
        });
    }

    public getSpecificEcuDidMapping(machinenumber: string, ecuId: number, line: string): Observable<t_DidConfig> {
        return this.http.get<t_DidConfig>(this.baseUrl + 'didconfig?machinenumber=' + machinenumber + '&ecu=' + ecuId + '&productline=' + line + 'line', {
            observe: 'body',
            responseType: 'json',
        });
    }

    public getEcuDidMappingByMachineNumber(machinenumber: string, line: string): Observable<t_DidConfig> {
        return this.http.get<t_DidConfig>(this.baseUrl + 'didconfig?machinenumber=' + machinenumber + '&productline=' + line + 'line', {
            observe: 'body',
            responseType: 'json',
        });
    }

    public getDidQueryList(): Observable<HttpResponse<Object>> {
        return this.http.get(this.baseUrl + 'dataresource/didslist', {
            observe: 'response',
            responseType: 'json',
        });
    }

    public addDidToQueryList<t_DataIdentifier>(dataIdentifier: t_DataIdentifier | t_DataIdentifier[]): Observable<HttpResponse<Object>> {        
        if (!Array.isArray(dataIdentifier)) {
            dataIdentifier = [dataIdentifier];
        }

        let requestBody = dataIdentifier;
        let headers = new HttpHeaders().set('Content-Type', 'application/json');


        return this.http.put(this.baseUrl + 'dataresource/didslist', requestBody, {
            observe: 'response',
            responseType: 'json',
            headers: headers,
        });
    }

    public postNewDidQueryList<t_DataIdentifier>(dataIdentifier: t_DataIdentifier | t_DataIdentifier[]): Observable<HttpResponse<Object>> {
        if (!Array.isArray(dataIdentifier)) {
            dataIdentifier = [dataIdentifier];
        }        
        
        let requestBody = dataIdentifier;
        let headers = new HttpHeaders().set('Content-Type', 'application/json');

        return this.http.post(this.baseUrl + 'dataresource/didslist', requestBody, {
            headers: headers,
            observe: 'response',
            responseType: 'json',
        });
    }

    public removeDidFromQueryList(dataIdentifier: t_DataIdentifier) {
        let requestBody = [dataIdentifier];
        let headers = new HttpHeaders().set('Content-Type', 'application/json');

        const params = new HttpParams().set('data', JSON.stringify(requestBody));

        return this.http.delete(this.baseUrl + 'dataresource/didslist', { 
            headers: headers,
            body: requestBody,
        });
    }


    // write new value to dataName
    public writeDataByID(ecuId: number, dataId: number, changedValue: number) {
        let requestBody = { 'data' : changedValue};
        let headers = new HttpHeaders().set('Content-Type', 'application/json');

        return this.http.post(this.baseUrl + 'dataresource/write/?ecuid=' + ecuId + '&dataid=' + dataId, requestBody, {
            headers: headers,
            observe: 'response',
            responseType: 'json',
        });
    }

}

/* ================================================ */
/* ==================== TYPES ===================== */
/* ================================================ */

export type EcuCheckbox = t_Ecu & {
    checked: boolean;
};

export type t_DidConfig = t_Ecu[];

export type t_Ecu = {
    ecu_name: string;
    ecu_id: number;
    ecu_dids: t_Did[];
};

export type t_Did = {
    ecu_id?: number;
    did: number;
    did_name: string;
    readWrite: string;
    readLevel: string;
    writeLevel: string;
    unit: string;
    dataType: string;
    conversionFactor: number;
    conversionOffset: number;
};
